import { inject } from "mobx-react";
import { useEffect, useRef, useCallback } from "react";
import * as PlayerJs from "player.js";
import stringify from "fast-json-stable-stringify";

import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { CatalogKindTypes, generateCatalogUrl } from "../lib/catalog";
import { ListenLive, ListenLiveEvent } from "../lib/listenLive";
import { ILog } from "@ihr-radioedit/inferno-core";
import styles from "./IHRPlayerBar.module.scss";
import { Store } from "../../../client/stores";

const log = ILog.logger("IHRPlayerBarComponent");

interface IHRPlayerBarProps {
    store?: Store;
}

export const IHRPlayerBar = inject("store")(({ store }: IHRPlayerBarProps) => {
    if (!store) {
        return null;
    }

    const { site, env, api_base_uri, player } = store;
    const design = site.config.sections?.design;

    if (!design || !design.ihr_embed_player_bar_switch || !design.ihr_embed_player_bar_stream_theme) {
        return null;
    }

    const stream: Webapi.Maybe<Pick<Webapi.CatalogPointerRef, "id" | "kind">> = {
        id: site.getStreamId() || "",
        kind: "station",
    };

    const override: Webapi.Maybe<Pick<Webapi.CatalogPointerRef, "id" | "kind">> =
        design.ihr_embed_player_bar_stream_override || stream;

    const iframeUrl = generateCatalogUrl(env, {
        id: override.id,
        kind: CatalogKindTypes[override.kind],
        embed: true,
        proxy: true,
        api_base_uri,
        query: {
            sc: "inferno",
            campid: "b",
            pname: site.index.slug.toUpperCase() || "",
            theme: design.ihr_embed_player_bar_stream_theme || "light",
            configOverride:
                env.ENVIRONMENT === "stage"
                    ? stringify({
                          api: { client: `https://${env.AMP_HOST}`, server: `https://${env.AMP_HOST}` },
                      })
                    : undefined,
        },
    });

    if (!iframeUrl) {
        return null;
    }

    const playerJsRef = useRef<PlayerJs.Player | null>(null);

    const registerEvents = useCallback(() => {
        const iframeEl = document.querySelector("#ihr-player-bar-frame") as HTMLIFrameElement;
        if (iframeEl) {
            iframeEl.onclick = () => {
                const playerjs = new PlayerJs.Player(iframeEl);
                playerJsRef.current = playerjs;
                playerjs.on(ListenLive.READY, (data: PlayerJs.readyData) => {
                    playerjs.ready(data);
                    player.onReady();
                });
                playerjs.on(ListenLive.PLAY, () => player.onPlay());
                playerjs.on(ListenLive.PAUSE, () => player.onPause());
                playerjs.on(ListenLive.ENDED, () => player.onPause());
                playerjs.on(ListenLive.PROGRESS, () => player.onProgress());
                playerjs.on(ListenLive.ERROR, (data: PlayerJs.errorData) => {
                    if (data.event === ListenLive.ERROR) {
                        log.error(data.value);
                    }
                    player.onPause();
                });
            };
            iframeEl.click();
        }
    }, [player]);

    const handleExternalAction = useCallback((event: ListenLiveEvent) => {
        switch (event.action) {
            case ListenLive.PLAY:
                playerJsRef?.current?.play();
                break;
            case ListenLive.PAUSE:
                playerJsRef?.current?.pause();
                break;
            default:
                log.debug("Invalid action");
                break;
        }
    }, []);

    useEffect(() => {
        log.info("Registering IHR Player Events");
        registerEvents();
        player.externalAction.subscribe(handleExternalAction);
        return () => player.externalAction.unsubscribe(handleExternalAction);
    }, [handleExternalAction, registerEvents, player.externalAction]);

    return (
        <div className={styles.ihrPlayerBar}>
            <div className={styles.ihrPlayerBarContent}>
                <iframe
                    id="ihr-player-bar-frame"
                    title="Listen on iHeartRadio"
                    src={iframeUrl}
                    allow="autoplay"
                    allow-same-origin="true"
                    allow-scripts="true"
                />
            </div>
        </div>
    );
});

export default IHRPlayerBar;
